import React from "react";
import DataTable from "react-data-table-component";
import { Box } from "@mui/material";
import { Loader } from "./CustomLoader";
import CustomPagination from "./CustomPagination";
import { getListPageTableCustomStyles } from "../utils/constants";

/**
 * CommonDataTable
 * Standardized reusable DataTable wrapper for MuttzNutz admin panel.
 *
 * @param {Array} columns - Table column definitions
 * @param {Array} data - Array of row items
 * @param {boolean} isLoading - Loading state for progress indicator
 * @param {number} totalCount - Total rows for server-side pagination
 * @param {number} currentPage - Active page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {Function} onPageChange - Callback when page changes (page => void)
 * @param {Function} onRowsPerPageChange - Callback when rows per page changes (currentRowsPerPage, currentPage) => void
 * @param {Array} pageSizeOptions - Available rows per page options [10, 20, 50]
 * @param {boolean} pagination - Whether pagination is enabled (default true)
 * @param {boolean} paginationServer - Whether pagination is handled server-side (default true)
 * @param {boolean} selectableRows - Enable row selection checkboxes
 * @param {Function} onSelectedRowsChange - Selection change handler ({ allSelected, selectedCount, selectedRows }) => void
 * @param {boolean} clearSelectedRows - State flag to toggle clearing selected rows
 * @param {boolean} highlightOnHover - Enable row hover highlight (default true)
 * @param {boolean} responsive - Enable table responsiveness (default true)
 * @param {number} minWidth - Table min-width in px for responsive scroll wrapper (default 1000)
 * @param {object} customStyles - Optional customStyles override
 * @param {string} wrapperClassName - Wrapper container class (default "list-page-table-wrapper")
 * @param {string} className - DataTable class (default "list-page-table custom")
 * @param {React.ReactNode} noDataComponent - Custom empty state component
 */
const normalizeColumns = (cols = []) => {
  return cols.map((col) => {
    if (!col) return col;
    const nameLower = (typeof col.name === "string" ? col.name : "").toLowerCase().trim();

    // Action column: always centered with standard 170px width
    if (nameLower === "action" || nameLower === "actions" || col.id === "actions") {
      return {
        ...col,
        center: true,
        width: col.width || "170px",
        minWidth: col.minWidth || "170px",
        maxWidth: col.maxWidth || "170px",
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      };
    }

    // Serial number column: always centered with 70px width
    if (
      nameLower === "s.no." ||
      nameLower === "s.no" ||
      nameLower === "s no" ||
      nameLower === "sno" ||
      nameLower === "#"
    ) {
      return {
        ...col,
        center: true,
        width: col.width || "70px",
        minWidth: col.minWidth || "70px",
        maxWidth: col.maxWidth || "80px",
      };
    }

    // Profile / Avatar / Image / Logo column: centered with 80px width
    if (
      nameLower === "profile" ||
      nameLower === "avatar" ||
      nameLower === "image" ||
      nameLower === "icon" ||
      nameLower === "photo" ||
      nameLower === "logo"
    ) {
      return {
        ...col,
        center: true,
        width: col.width || "80px",
        minWidth: col.minWidth || "80px",
        maxWidth: col.maxWidth || "90px",
      };
    }

    // All data / text / status / chip columns: default to clean left-alignment with responsive text wrapping
    return {
      ...col,
      center: col.center !== undefined ? col.center : false,
      wrap: col.wrap !== undefined ? col.wrap : true,
      minWidth: col.minWidth || col.width || "140px",
    };
  });
};

const CommonDataTable = ({
  columns = [],
  data = [],
  isLoading = false,
  totalCount,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
  onRowsPerPageChange,
  pageSizeOptions = [10, 20, 50],
  pagination = true,
  paginationServer,
  selectableRows = false,
  onSelectedRowsChange,
  clearSelectedRows = false,
  highlightOnHover = true,
  responsive = true,
  minWidth,
  customStyles,
  wrapperClassName = "list-page-table-wrapper",
  className = "list-page-table custom",
  noDataComponent,
  ...rest
}) => {
  const isServerPagination =
    paginationServer !== undefined ? paginationServer : Boolean(onPageChange);
  const totalRows =
    totalCount !== undefined && totalCount !== null ? totalCount : data.length;
  
  // Smart responsive minWidth calculation based on column count and declared column widths
  const computedMinWidth = React.useMemo(() => {
    if (minWidth !== undefined) return minWidth;
    let total = 0;
    (columns || []).forEach((col) => {
      if (!col) return;
      const w = col.minWidth || col.width;
      if (typeof w === "number") {
        total += w;
      } else if (typeof w === "string" && w.endsWith("px")) {
        total += parseInt(w, 10) || 150;
      } else {
        total += 160;
      }
    });
    const colCount = columns?.length || 5;
    return Math.max(1100, Math.max(total, colCount * 150));
  }, [minWidth, columns]);

  const tableStyles =
    customStyles || getListPageTableCustomStyles(computedMinWidth);
  const normalizedColumns = React.useMemo(
    () => normalizeColumns(columns),
    [columns]
  );

  return (
    <Box className={wrapperClassName}>
      <DataTable
        columns={normalizedColumns}
        data={data}
        progressPending={isLoading}
        progressComponent={<Loader />}
        pagination={pagination}
        paginationServer={isServerPagination}
        paginationTotalRows={totalRows}
        onChangePage={onPageChange}
        paginationPerPage={pageSize}
        paginationRowsPerPageOptions={pageSizeOptions}
        onChangeRowsPerPage={onRowsPerPageChange}
        highlightOnHover={highlightOnHover}
        responsive={responsive}
        selectableRows={selectableRows}
        onSelectedRowsChange={onSelectedRowsChange}
        clearSelectedRows={clearSelectedRows}
        paginationComponent={
          pagination
            ? (props) => (
                <CustomPagination
                  {...props}
                  currentPage={props.currentPage || currentPage}
                />
              )
            : undefined
        }
        customStyles={tableStyles}
        className={className}
        noDataComponent={noDataComponent}
        {...rest}
      />
    </Box>
  );
};

export default CommonDataTable;
